import api from './api';
import { nullableUuid } from '@/utils/uuid';

export interface RoteiroOperacao {
  id?: string;
  sequencia: number;
  operacao: string;
  centro_trabalho_id?: string | null;
  tempo_padrao_minutos?: number | null;
  tempo_setup_minutos?: number | null;
}

export interface Roteiro {
  id: string;
  produto_id: string;
  produto_codigo?: string | null;
  produto_descricao?: string | null;
  produto_nome?: string | null;
  versao: string;
  tempo_total_padrao?: number | null;
  status: 'RASCUNHO' | 'APROVADO' | 'OBSOLETO';
  data_aprovacao?: string | null;
  aprovado_por?: string | null;
  operacoes: RoteiroOperacao[];
}

export interface RoteiroInput {
  produto_id: string;
  versao: string;
  tempo_total_padrao?: number | null;
  status?: string;
  operacoes?: RoteiroOperacao[];
}

const ENDPOINT = '/industrializacao/roteiro';

function normalizeRoteiroPayload<T extends Partial<RoteiroInput>>(data: T): T {
  const payload = {
    ...data,
    operacoes: data.operacoes?.map((operacao) => ({
      ...operacao,
      centro_trabalho_id: nullableUuid(operacao.centro_trabalho_id),
    })),
  };

  if ('produto_id' in data) {
    return {
      ...payload,
      produto_id: nullableUuid(data.produto_id) || '',
    };
  }

  return payload;
}

const roteiroService = {
  async listar(filters: { produto?: string; status?: string; search?: string } = {}): Promise<Roteiro[]> {
    const response = await api.get<Roteiro[]>(ENDPOINT, { params: filters });
    return response.data;
  },
  async buscarPorId(id: string): Promise<Roteiro> {
    const response = await api.get<Roteiro>(`${ENDPOINT}/${id}`);
    return response.data;
  },
  async criar(data: RoteiroInput): Promise<Roteiro> {
    const response = await api.post<Roteiro>(ENDPOINT, normalizeRoteiroPayload(data));
    return response.data;
  },
  async atualizar(id: string, data: Partial<RoteiroInput>): Promise<Roteiro> {
    const response = await api.put<Roteiro>(`${ENDPOINT}/${id}`, normalizeRoteiroPayload(data));
    return response.data;
  },
  async excluir(id: string): Promise<void> {
    await api.delete(`${ENDPOINT}/${id}`);
  },
  async novaVersao(id: string, versao: string): Promise<Roteiro> {
    const response = await api.post<Roteiro>(`${ENDPOINT}/${id}/nova_versao`, { versao });
    return response.data;
  },
  async aprovar(id: string): Promise<Roteiro> {
    const response = await api.post<Roteiro>(`${ENDPOINT}/${id}/aprovar`);
    return response.data;
  },
};

export default roteiroService;
